'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

type VideoAdStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'ended';
type VideoAdPlacement = 'pre-roll' | 'mid-roll' | 'post-roll' | 'sidebar' | 'between-videos';
type VideoAdCreativeType = 'video' | 'image';

interface VideoAd {
  id: string;
  title: string;
  advertiser: string;
  video_url: string;
  image_url: string;
  thumbnail_url: string;
  thumbnail_alt: string;
  link_url: string;
  placement: VideoAdPlacement;
  ad_status: VideoAdStatus;
  creative_type: VideoAdCreativeType;
  duration_seconds: number;
  skip_after_seconds: number;
  schedule_start: string | null;
  schedule_end: string | null;
  target_categories: string[] | null;
  impressions: number;
  clicks: number;
  notes: string;
  created_at: string;
}

const STATUS_CONFIG: Record<VideoAdStatus, { label: string; color: string; bg: string }> = {
  draft:     { label: 'Draft',     color: 'text-text-muted',    bg: 'bg-surface-overlay' },
  scheduled: { label: 'Scheduled', color: 'text-blue-400',      bg: 'bg-blue-900/30' },
  active:    { label: 'Active',    color: 'text-green-400',     bg: 'bg-green-900/30' },
  paused:    { label: 'Paused',    color: 'text-yellow-400',    bg: 'bg-yellow-900/30' },
  ended:     { label: 'Ended',     color: 'text-text-secondary', bg: 'bg-surface-tertiary' },
};

const PLACEMENT_LABELS: Record<VideoAdPlacement, string> = {
  'pre-roll':        'Pre-Roll',
  'mid-roll':        'Mid-Roll',
  'post-roll':       'Post-Roll',
  'sidebar':         'Sidebar',
  'between-videos':  'Between Videos',
};

const EMPTY_FORM = {
  title: '',
  advertiser: '',
  video_url: '',
  image_url: '',
  thumbnail_url: '',
  thumbnail_alt: '',
  link_url: '',
  placement: 'pre-roll' as VideoAdPlacement,
  ad_status: 'draft' as VideoAdStatus,
  creative_type: 'video' as VideoAdCreativeType,
  duration_seconds: 30,
  skip_after_seconds: 5,
  schedule_start: '',
  schedule_end: '',
  target_categories: '',
  notes: '',
};

export default function VideoAdsAdminPage() {
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  // Auth state
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Data state
  const [ads, setAds] = useState<VideoAd[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Filter state
  const [filterStatus, setFilterStatus] = useState<VideoAdStatus | 'all'>('all');
  const [filterPlacement, setFilterPlacement] = useState<VideoAdPlacement | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState<VideoAd | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Video file upload
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Thumbnail upload
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState<string | null>(null);
  const thumbInputRef = useRef<HTMLInputElement>(null);

  // Delete confirm
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Preview modal
  const [previewAd, setPreviewAd] = useState<VideoAd | null>(null);

  // Edit stats modal
  const [editStatsAd, setEditStatsAd] = useState<VideoAd | null>(null);
  const [editStatsForm, setEditStatsForm] = useState({ impressions: 0, clicks: 0 });
  const [editStatsLoading, setEditStatsLoading] = useState(false);
  const [editStatsError, setEditStatsError] = useState('');

  // Thumbnail generation
  const [generatingThumbId, setGeneratingThumbId] = useState<string | null>(null);
  const thumbGenVideoRef = useRef<HTMLVideoElement>(null);
  const thumbGenCanvasRef = useRef<HTMLCanvasElement>(null);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const handleAuth = async () => {
    if (!password.trim()) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.success) {
        setAuthenticated(true);
      } else {
        setAuthError('Incorrect password.');
      }
    } catch {
      setAuthError('Authentication failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchAds = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('video_ads')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setAds(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load video ads');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (authenticated) fetchAds();
  }, [authenticated, fetchAds]);

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setVideoFile(file);
    if (file) {
      setVideoPreviewUrl(URL.createObjectURL(file));
    } else {
      setVideoPreviewUrl(null);
    }
  };

  const handleThumbFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setThumbFile(file);
    if (file) {
      setThumbPreviewUrl(URL.createObjectURL(file));
    } else {
      setThumbPreviewUrl(null);
    }
  };

  const openForm = (ad?: VideoAd) => {
    if (ad) {
      setEditingAd(ad);
      setForm({
        title: ad.title,
        advertiser: ad.advertiser,
        video_url: ad.video_url,
        thumbnail_url: ad.thumbnail_url,
        thumbnail_alt: ad.thumbnail_alt,
        link_url: ad.link_url,
        placement: ad.placement,
        ad_status: ad.ad_status,
        creative_type: (ad.creative_type || 'video') as VideoAdCreativeType,
        duration_seconds: ad.duration_seconds,
        skip_after_seconds: ad.skip_after_seconds,
        schedule_start: ad.schedule_start ? ad.schedule_start.slice(0, 16) : '',
        schedule_end: ad.schedule_end ? ad.schedule_end.slice(0, 16) : '',
        target_categories: ad.target_categories ? ad.target_categories.join(', ') : '',
        image_url: ad.image_url || '',
        notes: ad.notes,
      });
    } else {
      setEditingAd(null);
      setForm(EMPTY_FORM);
    }
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setThumbFile(null);
    setThumbPreviewUrl(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (thumbInputRef.current) thumbInputRef.current.value = '';
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingAd(null);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setFormError('Ad title is required.'); return; }
    if (!form.link_url.trim()) { setFormError('Destination URL is required.'); return; }
    if (form.creative_type === 'video' && !editingAd && !form.video_url.trim() && !videoFile) {
      setFormError('A video file or video URL is required for video ads.'); return;
    }
    if (form.creative_type === 'image' && !thumbFile && !form.thumbnail_url.trim()) {
      setFormError('Please upload an image for the image ad.'); return;
    }
    setFormLoading(true);
    setFormError('');
    try {
      let videoUrl = form.video_url.trim();
      let thumbnailUrl = form.thumbnail_url.trim();

      if (videoFile) {
        const fileName = `video-ads/${crypto.randomUUID()}-${videoFile.name}`;
        const { error: uploadErr } = await supabase.storage
          .from('video-ads')
          .upload(fileName, videoFile, { upsert: false });
        if (uploadErr) throw uploadErr;
        const { data: urlData } = supabase.storage.from('video-ads').getPublicUrl(fileName);
        videoUrl = urlData.publicUrl;
      }

      if (thumbFile) {
        const thumbName = `video-ads/thumbs/${crypto.randomUUID()}-${thumbFile.name}`;
        const { error: thumbErr } = await supabase.storage
          .from('video-ads')
          .upload(thumbName, thumbFile, { upsert: false });
        if (thumbErr) throw thumbErr;
        const { data: thumbUrlData } = supabase.storage.from('video-ads').getPublicUrl(thumbName);
        thumbnailUrl = thumbUrlData.publicUrl;
      }

      // For image ads, the uploaded/provided thumbnail IS the image ad creative
      const imageUrl = form.creative_type === 'image' ? thumbnailUrl : form.image_url.trim();

      const payload = {
        title: form.title.trim(),
        advertiser: form.advertiser.trim(),
        video_url: videoUrl,
        image_url: imageUrl,
        thumbnail_url: thumbnailUrl,
        thumbnail_alt: form.thumbnail_alt.trim() || form.title.trim(),
        link_url: form.link_url.trim(),
        placement: form.placement,
        ad_status: form.ad_status,
        creative_type: form.creative_type,
        duration_seconds: Number(form.duration_seconds) || 30,
        skip_after_seconds: Number(form.skip_after_seconds) || 5,
        schedule_start: form.schedule_start ? new Date(form.schedule_start).toISOString() : null,
        schedule_end: form.schedule_end ? new Date(form.schedule_end).toISOString() : null,
        target_categories: form.target_categories.trim()
          ? form.target_categories.split(',').map((c: string) => c.trim()).filter(Boolean)
          : null,
        notes: form.notes.trim(),
        updated_at: new Date().toISOString(),
      };

      if (editingAd) {
        const { error: err } = await supabase.from('video_ads').update(payload).eq('id', editingAd.id);
        if (err) throw err;
        showSuccess('Video ad updated successfully!');
      } else {
        const { error: err } = await supabase.from('video_ads').insert({ id: crypto.randomUUID(), ...payload, impressions: 0, clicks: 0 });
        if (err) throw err;
        showSuccess('Video ad created successfully!');
      }
      closeForm();
      fetchAds();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save video ad');
    } finally {
      setFormLoading(false);
    }
  };

  const handleStatusChange = async (ad: VideoAd, newStatus: VideoAdStatus) => {
    try {
      const { error: err } = await supabase
        .from('video_ads')
        .update({ ad_status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', ad.id);
      if (err) throw err;
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, ad_status: newStatus } : a));
      showSuccess(`Ad status changed to ${STATUS_CONFIG[newStatus].label}`);
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleDelete = async (adId: string) => {
    setDeleteLoading(true);
    try {
      const { error: err } = await supabase.from('video_ads').delete().eq('id', adId);
      if (err) throw err;
      setAds(prev => prev.filter(a => a.id !== adId));
      setDeleteConfirmId(null);
      showSuccess('Video ad deleted successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to delete video ad');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSaveAdStats = async () => {
    if (!editStatsAd) return;
    setEditStatsLoading(true);
    setEditStatsError('');
    try {
      const { error: err } = await supabase
        .from('video_ads')
        .update({ impressions: editStatsForm.impressions, clicks: editStatsForm.clicks, updated_at: new Date().toISOString() })
        .eq('id', editStatsAd.id);
      if (err) throw err;
      setAds(prev => prev.map(a => a.id === editStatsAd.id ? { ...a, impressions: editStatsForm.impressions, clicks: editStatsForm.clicks } : a));
      setEditStatsAd(null);
      showSuccess('Ad stats updated successfully');
    } catch (err: any) {
      setEditStatsError(err.message || 'Failed to update stats');
    } finally {
      setEditStatsLoading(false);
    }
  };

  const filteredAds = ads.filter(ad => {
    if (filterStatus !== 'all' && ad.ad_status !== filterStatus) return false;
    if (filterPlacement !== 'all' && ad.placement !== filterPlacement) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return ad.title.toLowerCase().includes(q) || ad.advertiser.toLowerCase().includes(q);
    }
    return true;
  });

  const statCounts = {
    total: ads.length,
    active: ads.filter(a => a.ad_status === 'active').length,
    scheduled: ads.filter(a => a.ad_status === 'scheduled').length,
    draft: ads.filter(a => a.ad_status === 'draft').length,
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const ctr = (ad: VideoAd) => {
    if (!ad.impressions) return '0.00%';
    return ((ad.clicks / ad.impressions) * 100).toFixed(2) + '%';
  };

  const handleGenerateThumbnail = async (ad: VideoAd) => {
    if (!ad.video_url) return;
    setGeneratingThumbId(ad.id);
    try {
      await new Promise<void>((resolve, reject) => {
        const video = thumbGenVideoRef.current;
        const canvas = thumbGenCanvasRef.current;
        if (!video || !canvas) { reject(new Error('Canvas not ready')); return; }

        video.crossOrigin = 'anonymous';
        video.src = ad.video_url;
        video.muted = true;

        const onError = () => reject(new Error('Failed to load video for thumbnail generation'));

        const onLoaded = () => {
          // Seek to 10% of duration or 2 seconds, whichever is smaller
          const seekTo = Math.min(video.duration * 0.1, 2);
          video.currentTime = isFinite(seekTo) && seekTo > 0 ? seekTo : 0;
        };

        const onSeeked = async () => {
          try {
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('Canvas context unavailable')); return; }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', 0.85));
            if (!blob) { reject(new Error('Failed to capture frame')); return; }

            const fileName = `video-ads/thumbs/auto-${ad.id}-${Date.now()}.jpg`;
            const { error: uploadErr } = await supabase.storage
              .from('video-ads')
              .upload(fileName, blob, { upsert: true, contentType: 'image/jpeg' });
            if (uploadErr) { reject(uploadErr); return; }

            const { data: urlData } = supabase.storage.from('video-ads').getPublicUrl(fileName);
            const newThumbUrl = urlData.publicUrl;

            const { error: updateErr } = await supabase
              .from('video_ads')
              .update({ thumbnail_url: newThumbUrl, updated_at: new Date().toISOString() })
              .eq('id', ad.id);
            if (updateErr) { reject(updateErr); return; }

            setAds(prev => prev.map(a => a.id === ad.id ? { ...a, thumbnail_url: newThumbUrl } : a));
            showSuccess('Thumbnail generated and saved!');
            resolve();
          } catch (err) {
            reject(err);
          }
        };

        video.addEventListener('loadedmetadata', onLoaded, { once: true });
        video.addEventListener('seeked', onSeeked, { once: true });
        video.addEventListener('error', onError, { once: true });
        video.load();
      });
    } catch (err: any) {
      setError(err.message || 'Failed to generate thumbnail');
    } finally {
      setGeneratingThumbId(null);
      if (thumbGenVideoRef.current) {
        thumbGenVideoRef.current.src = '';
      }
    }
  };

  // ── Auth Gate ──────────────────────────────────────────────────────────────
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-surface-primary flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-surface-secondary border border-stroke-primary rounded-[4px] p-6">
          <div className="flex items-center gap-2 mb-6">
            <Link href="/" className="text-text-muted hover:text-text-secondary transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-text-primary font-bold text-lg uppercase tracking-wide">Video Ads Admin</h1>
          </div>
          <p className="text-text-muted text-xs uppercase tracking-wide mb-4">Enter admin password to continue</p>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAuth()}
            placeholder="Admin password"
            className="input-field w-full mb-3"
            autoFocus
          />
          {authError && <p className="text-brand-red text-sm mb-3">{authError}</p>}
          <button
            onClick={handleAuth}
            disabled={authLoading || !password.trim()}
            className="btn-primary w-full justify-center disabled:opacity-60"
          >
            {authLoading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Verifying…
              </span>
            ) : 'Access Admin'}
          </button>
        </div>
      </div>
    );
  }

  // ── Main Admin Screen ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-primary text-text-primary">
      {/* Header */}
      <header className="bg-surface-secondary border-b border-stroke-primary sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/" className="text-text-muted hover:text-text-secondary transition-colors p-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div className="w-px h-4 bg-stroke-primary" />
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.069A1 1 0 0121 8.882v6.236a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h1 className="text-text-primary font-bold text-base uppercase tracking-wide">Video Ads Manager</h1>
            </div>
          </div>
          <button
            onClick={() => openForm()}
            className="btn-primary text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Video Ad
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Success / Error banners */}
        {successMsg && (
          <div className="mb-4 px-4 py-2.5 bg-green-900/30 border border-green-700 rounded-[4px] text-green-400 text-sm flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMsg}
          </div>
        )}
        {error && (
          <div className="mb-4 px-4 py-2.5 bg-red-900/30 border border-red-700 rounded-[4px] text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Ads', value: statCounts.total, icon: 'M15 10l4.553-2.069A1 1 0 0121 8.882v6.236a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
            { label: 'Active', value: statCounts.active, icon: 'M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z', accent: true },
            { label: 'Scheduled', value: statCounts.scheduled, icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
            { label: 'Drafts', value: statCounts.draft, icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
          ].map(stat => (
            <div key={stat.label} className="bg-surface-secondary border border-stroke-primary rounded-[4px] px-4 py-3 flex items-center gap-3">
              <div className={`w-8 h-8 rounded-[4px] flex items-center justify-center flex-shrink-0 ${stat.accent ? 'bg-brand-red/20' : 'bg-surface-tertiary'}`}>
                <svg className={`w-4 h-4 ${stat.accent ? 'text-brand-red' : 'text-text-secondary'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={stat.icon} />
                </svg>
              </div>
              <div>
                <p className="text-text-primary text-xl font-bold leading-none">{stat.value}</p>
                <p className="text-text-muted text-xs uppercase tracking-wide mt-0.5">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by title or advertiser…"
            className="input-field text-sm flex-1 min-w-[200px]"
          />
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as VideoAdStatus | 'all')}
            className="input-field text-sm"
          >
            <option value="all">All Statuses</option>
            {(Object.keys(STATUS_CONFIG) as VideoAdStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>
          <select
            value={filterPlacement}
            onChange={e => setFilterPlacement(e.target.value as VideoAdPlacement | 'all')}
            className="input-field text-sm"
          >
            <option value="all">All Placements</option>
            {(Object.keys(PLACEMENT_LABELS) as VideoAdPlacement[]).map(p => (
              <option key={p} value={p}>{PLACEMENT_LABELS[p]}</option>
            ))}
          </select>
          <button
            onClick={fetchAds}
            disabled={loading}
            className="text-text-muted hover:text-text-secondary text-xs flex items-center gap-1 transition-colors px-2 disabled:opacity-50"
          >
            <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        <p className="text-text-muted text-xs uppercase tracking-wide mb-3">
          {loading ? 'Loading…' : `${filteredAds.length} ad${filteredAds.length !== 1 ? 's' : ''}`}
        </p>

        {/* Ads Table */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-text-muted text-sm">Loading video ads…</p>
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="text-center py-16 bg-surface-secondary border border-stroke-primary rounded-[4px]">
            <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.069A1 1 0 0021 8.882v6.236a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-text-secondary text-base">No video ads found</p>
            <p className="text-text-muted text-sm mt-1">Click "New Video Ad" to create your first ad</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAds.map(ad => {
              const statusCfg = STATUS_CONFIG[ad.ad_status];
              return (
                <div key={ad.id} className="bg-surface-secondary border border-stroke-primary rounded-[4px] p-4">
                  <div className="flex flex-col md:flex-row md:items-start gap-3">
                    {/* Thumbnail */}
                    <div className="w-full md:w-28 h-16 bg-surface-tertiary rounded-[4px] overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {ad.thumbnail_url ? (
                        <img src={ad.thumbnail_url} alt={ad.thumbnail_alt || ad.title} className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-6 h-6 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.882v6.236a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="text-text-primary font-bold text-sm truncate">{ad.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-[4px] font-bold uppercase tracking-wide ${statusCfg.bg} ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-[4px] bg-surface-overlay text-text-secondary uppercase tracking-wide">
                          {PLACEMENT_LABELS[ad.placement]}
                        </span>
                      </div>
                      {ad.advertiser && (
                        <p className="text-text-muted text-xs mb-1">{ad.advertiser}</p>
                      )}
                      <div className="flex flex-wrap gap-3 text-xs text-text-muted">
                        <span>{ad.duration_seconds}s duration · skip after {ad.skip_after_seconds}s</span>
                        <span>Impressions: <span className="text-text-secondary">{ad.impressions.toLocaleString()}</span></span>
                        <span>Clicks: <span className="text-text-secondary">{ad.clicks.toLocaleString()}</span></span>
                        <span>CTR: <span className="text-text-secondary">{ctr(ad)}</span></span>
                      </div>
                      {(ad.schedule_start || ad.schedule_end) && (
                        <div className="flex flex-wrap gap-3 text-xs text-text-muted mt-1">
                          {ad.schedule_start && <span>Start: <span className="text-blue-400">{formatDate(ad.schedule_start)}</span></span>}
                          {ad.schedule_end && <span>End: <span className="text-blue-400">{formatDate(ad.schedule_end)}</span></span>}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0">
                      {/* Status quick-change */}
                      <select
                        value={ad.ad_status}
                        onChange={e => handleStatusChange(ad, e.target.value as VideoAdStatus)}
                        className="input-field text-xs py-1 px-2 h-auto"
                        title="Change status"
                      >
                        {(Object.keys(STATUS_CONFIG) as VideoAdStatus[]).map(s => (
                          <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                        ))}
                      </select>

                      {/* Generate Thumbnail */}
                      {ad.video_url && (
                        <button
                          onClick={() => handleGenerateThumbnail(ad)}
                          disabled={generatingThumbId === ad.id}
                          className="text-xs px-2.5 py-1.5 border border-stroke-primary text-text-secondary hover:text-yellow-400 hover:border-yellow-600 rounded-[4px] transition-colors disabled:opacity-60"
                          title="Auto-generate thumbnail from video"
                        >
                          {generatingThumbId === ad.id ? (
                            <div className="w-3.5 h-3.5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      )}

                      {/* Preview */}
                      {ad.video_url && (
                        <button
                          onClick={() => setPreviewAd(ad)}
                          className="text-xs px-2.5 py-1.5 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
                          title="Preview video"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      )}

                      {/* Edit */}
                      <button
                        onClick={() => openForm(ad)}
                        className="text-xs px-2.5 py-1.5 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
                        title="Edit ad"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>

                      {/* Edit Stats */}
                      <button
                        onClick={() => { setEditStatsAd(ad); setEditStatsForm({ impressions: ad.impressions, clicks: ad.clicks }); setEditStatsError(''); }}
                        className="text-xs px-2.5 py-1.5 border border-stroke-primary text-text-secondary hover:text-blue-400 hover:border-blue-600 rounded-[4px] transition-colors"
                        title="Edit impressions & clicks"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </button>

                      {/* Delete */}
                      {deleteConfirmId === ad.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-text-muted">Delete?</span>
                          <button
                            onClick={() => handleDelete(ad.id)}
                            disabled={deleteLoading}
                            className="text-xs font-bold px-2.5 py-1.5 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-60"
                          >
                            {deleteLoading ? '…' : 'Yes'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs font-bold px-2.5 py-1.5 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(ad.id)}
                          className="text-xs px-2.5 py-1.5 border border-stroke-primary text-text-muted hover:text-brand-red hover:border-brand-red rounded-[4px] transition-colors"
                          title="Delete ad"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Create / Edit Form Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 overflow-y-auto py-8 px-4">
          <div className="w-full max-w-2xl bg-surface-secondary border border-stroke-primary rounded-[4px] shadow-modal">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary">
              <h2 className="text-text-primary font-bold text-sm uppercase tracking-wide">
                {editingAd ? 'Edit Video Ad' : 'New Video Ad'}
              </h2>
              <button onClick={closeForm} className="text-text-muted hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Video Upload — only shown for video ads */}
              {form.creative_type === 'video' && (
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                  Video File {!editingAd && <span className="text-brand-red">*</span>}
                </label>
                <div
                  onClick={() => videoInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-[4px] p-4 text-center cursor-pointer transition-colors ${
                    videoFile ? 'border-brand-red bg-brand-red/5' : 'border-stroke-primary hover:border-stroke-light'
                  }`}
                >
                  {videoPreviewUrl ? (
                    <div className="space-y-2">
                      <video src={videoPreviewUrl} className="max-h-28 mx-auto rounded object-contain" controls />
                      <p className="text-text-secondary text-xs truncate">{videoFile?.name}</p>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setVideoFile(null); setVideoPreviewUrl(null); if (videoInputRef.current) videoInputRef.current.value = ''; }}
                        className="text-brand-red text-xs hover:underline"
                      >Remove</button>
                    </div>
                  ) : (
                    <div className="py-2">
                      <svg className="w-7 h-7 text-text-muted mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.882v6.236a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <p className="text-text-secondary text-sm">Click to upload video</p>
                      <p className="text-text-muted text-xs mt-0.5">MP4, WebM, OGG — up to 500MB</p>
                    </div>
                  )}
                </div>
                <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/ogg" onChange={handleVideoFileChange} className="hidden" />
                <p className="text-text-muted text-xs mt-1.5">Or paste a video URL below:</p>
                <input
                  type="url"
                  value={form.video_url}
                  onChange={e => setForm(p => ({ ...p, video_url: e.target.value }))}
                  placeholder="https://…"
                  className="input-field mt-1.5 text-sm"
                  disabled={!!videoFile}
                />
              </div>
              )}

              {/* Thumbnail / Image Ad */}
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                  {form.creative_type === 'image' ? (
                    <>Ad Image <span className="text-brand-red">*</span> <span className="normal-case font-normal text-text-muted">(this image will be displayed as the ad)</span></>
                  ) : 'Thumbnail Image'}
                </label>
                <div
                  onClick={() => thumbInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-[4px] p-3 text-center cursor-pointer transition-colors ${
                    thumbFile ? 'border-brand-red bg-brand-red/5' : 'border-stroke-primary hover:border-stroke-light'
                  }`}
                >
                  {thumbPreviewUrl ? (
                    <div className="space-y-1">
                      <img src={thumbPreviewUrl} alt="Thumbnail preview" className="max-h-20 mx-auto object-contain rounded" />
                      <p className="text-text-secondary text-xs truncate">{thumbFile?.name}</p>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setThumbFile(null); setThumbPreviewUrl(null); if (thumbInputRef.current) thumbInputRef.current.value = ''; }}
                        className="text-brand-red text-xs hover:underline"
                      >Remove</button>
                    </div>
                  ) : (
                    <div className="py-1">
                      <p className="text-text-secondary text-sm">Click to upload thumbnail</p>
                      <p className="text-text-muted text-xs">JPG, PNG, WebP</p>
                    </div>
                  )}
                </div>
                <input ref={thumbInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleThumbFileChange} className="hidden" />
                <input
                  type="url"
                  value={form.thumbnail_url}
                  onChange={e => setForm(p => ({ ...p, thumbnail_url: e.target.value }))}
                  placeholder="Or paste thumbnail URL…"
                  className="input-field mt-1.5 text-sm"
                  disabled={!!thumbFile}
                />
              </div>

              {/* Title + Advertiser */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Ad Title <span className="text-brand-red">*</span></label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Summer Sale 2026"
                    className="input-field text-sm"
                  />
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Advertiser</label>
                  <input
                    type="text"
                    value={form.advertiser}
                    onChange={e => setForm(p => ({ ...p, advertiser: e.target.value }))}
                    placeholder="Brand or company name"
                    className="input-field text-sm"
                  />
                </div>
              </div>

              {/* Thumbnail Alt */}
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Thumbnail Alt Text</label>
                <input
                  type="text"
                  value={form.thumbnail_alt}
                  onChange={e => setForm(p => ({ ...p, thumbnail_alt: e.target.value }))}
                  placeholder="Describe the thumbnail for accessibility"
                  className="input-field text-sm"
                />
              </div>

              {/* Link URL + Placement */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Destination URL <span className="text-brand-red">*</span></label>
                  <input
                    type="url"
                    value={form.link_url}
                    onChange={e => setForm(p => ({ ...p, link_url: e.target.value }))}
                    placeholder="https://advertiser.com/landing"
                    className="input-field text-sm"
                  />
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Placement</label>
                  <select
                    value={form.placement}
                    onChange={e => setForm(p => ({ ...p, placement: e.target.value as VideoAdPlacement }))}
                    className="input-field text-sm"
                  >
                    {(Object.keys(PLACEMENT_LABELS) as VideoAdPlacement[]).map(pl => (
                      <option key={pl} value={pl}>{PLACEMENT_LABELS[pl]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Status + Duration */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Status</label>
                  <select
                    value={form.ad_status}
                    onChange={e => setForm(p => ({ ...p, ad_status: e.target.value as VideoAdStatus }))}
                    className="input-field text-sm"
                  >
                    {(Object.keys(STATUS_CONFIG) as VideoAdStatus[]).map(s => (
                      <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Duration (sec)</label>
                  <input
                    type="number"
                    min={5}
                    max={300}
                    value={form.duration_seconds}
                    onChange={e => setForm(p => ({ ...p, duration_seconds: Number(e.target.value) }))}
                    className="input-field text-sm"
                  />
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Skip After (sec)</label>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={form.skip_after_seconds}
                    onChange={e => setForm(p => ({ ...p, skip_after_seconds: Number(e.target.value) }))}
                    className="input-field text-sm"
                  />
                </div>
              </div>

              {/* Creative Type */}
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Creative Type</label>
                <div className="flex gap-3">
                  {(['video', 'image'] as VideoAdCreativeType[]).map(ct => (
                    <label key={ct} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="creative_type"
                        value={ct}
                        checked={form.creative_type === ct}
                        onChange={() => setForm(p => ({ ...p, creative_type: ct }))}
                        className="accent-brand-red"
                      />
                      <span className="text-text-secondary text-sm capitalize">{ct} Ad</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Category Targeting */}
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                  Category Targeting
                  <span className="text-text-muted font-normal normal-case ml-1">(optional — leave blank to show on all videos)</span>
                </label>
                <input
                  type="text"
                  value={form.target_categories}
                  onChange={e => setForm(p => ({ ...p, target_categories: e.target.value }))}
                  placeholder="e.g. Sports, Music, News (comma-separated)"
                  className="input-field text-sm"
                />
              </div>

              {/* Schedule */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Schedule Start</label>
                  <input
                    type="datetime-local"
                    value={form.schedule_start}
                    onChange={e => setForm(p => ({ ...p, schedule_start: e.target.value }))}
                    className="input-field text-sm"
                  />
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Schedule End</label>
                  <input
                    type="datetime-local"
                    value={form.schedule_end}
                    onChange={e => setForm(p => ({ ...p, schedule_end: e.target.value }))}
                    className="input-field text-sm"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Internal Notes</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Paid $1,200 for 30 days, contact: jane@brand.com"
                  className="input-field text-sm"
                />
              </div>

              {formError && <p className="text-brand-red text-sm">{formError}</p>}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleSave}
                  disabled={formLoading}
                  className="btn-primary flex-1 justify-center disabled:opacity-60"
                >
                  {formLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {editingAd ? 'Save Changes' : 'Create Video Ad'}
                    </span>
                  )}
                </button>
                <button
                  onClick={closeForm}
                  className="px-4 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Video Preview Modal ── */}
      {previewAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreviewAd(null)}>
          <div className="w-full max-w-2xl bg-surface-secondary border border-stroke-primary rounded-[4px] shadow-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-stroke-primary">
              <div>
                <p className="text-text-primary font-bold text-sm">{previewAd.title}</p>
                {previewAd.advertiser && <p className="text-text-muted text-xs">{previewAd.advertiser}</p>}
              </div>
              <button onClick={() => setPreviewAd(null)} className="text-text-muted hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
              <video
                src={previewAd.video_url}
                controls
                autoPlay
                className="w-full h-full"
                poster={previewAd.thumbnail_url || undefined}
              />
            </div>
            <div className="px-4 py-3 flex flex-wrap gap-4 text-xs text-text-muted border-t border-stroke-primary">
              <span>Placement: <span className="text-text-secondary">{PLACEMENT_LABELS[previewAd.placement]}</span></span>
              <span>Duration: <span className="text-text-secondary">{previewAd.duration_seconds}s</span></span>
              <span>Skip after: <span className="text-text-secondary">{previewAd.skip_after_seconds}s</span></span>
              <span>CTR: <span className="text-text-secondary">{ctr(previewAd)}</span></span>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Ad Stats Modal ── */}
      {editStatsAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditStatsAd(null)} />
          <div className="relative w-full max-w-sm bg-surface-secondary border border-stroke-primary rounded-[4px] shadow-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary">
              <h2 className="text-text-primary font-bold text-sm uppercase tracking-wide">Edit Ad Stats</h2>
              <button onClick={() => setEditStatsAd(null)} className="text-text-muted hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-text-muted text-xs line-clamp-1 italic">{editStatsAd.title}</p>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Impressions</label>
                <input
                  type="number"
                  min={0}
                  value={editStatsForm.impressions}
                  onChange={e => setEditStatsForm(prev => ({ ...prev, impressions: Math.max(0, parseInt(e.target.value) || 0) }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Clicks</label>
                <input
                  type="number"
                  min={0}
                  value={editStatsForm.clicks}
                  onChange={e => setEditStatsForm(prev => ({ ...prev, clicks: Math.max(0, parseInt(e.target.value) || 0) }))}
                  className="input-field"
                />
              </div>
              {editStatsError && <p className="text-brand-red text-sm">{editStatsError}</p>}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleSaveAdStats}
                  disabled={editStatsLoading}
                  className="btn-primary flex-1 justify-center disabled:opacity-60"
                >
                  {editStatsLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving…
                    </span>
                  ) : 'Save Stats'}
                </button>
                <button
                  onClick={() => setEditStatsAd(null)}
                  className="px-4 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden elements for thumbnail generation */}
      <video ref={thumbGenVideoRef} className="hidden" />
      <canvas ref={thumbGenCanvasRef} className="hidden" />
    </div>
  );
}
